/**
 * Lucide-style inline icons as Vue 3 components.
 *
 * Each icon uses defineComponent + markRaw so it works correctly with:
 * - Direct template usage: <IconCheck :size="14" />
 * - Dynamic components: <component :is="icon" />
 * - Prop passing: <AppButton :icon="IconCheck" />
 */
import { defineComponent, h, markRaw } from 'vue'

function makeIcon(name, paths) {
  return markRaw(defineComponent({
    name,
    props: {
      size: { type: [Number, String], default: 18 }
    },
    render() {
      return h('svg', {
        xmlns: 'http://www.w3.org/2000/svg',
        width: this.size,
        height: this.size,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'currentColor',
        'stroke-width': '2',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round'
      }, paths.map(p => h(p[0], p[1])))
    }
  }))
}

export const IconDashboard = makeIcon('IconDashboard', [
  ['rect', { x: '3', y: '3', width: '7', height: '7' }],
  ['rect', { x: '14', y: '3', width: '7', height: '7' }],
  ['rect', { x: '14', y: '14', width: '7', height: '7' }],
  ['rect', { x: '3', y: '14', width: '7', height: '7' }]
])

export const IconUsers = makeIcon('IconUsers', [
  ['path', { d: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' }],
  ['circle', { cx: '9', cy: '7', r: '4' }],
  ['path', { d: 'M22 21v-2a4 4 0 0 0-3-3.87' }],
  ['path', { d: 'M16 3.13a4 4 0 0 1 0 7.75' }]
])

export const IconMap = makeIcon('IconMap', [
  ['path', { d: 'M1 6v16l7-3 8 3 7-3V3l-7 3-8-3-7 3z' }],
  ['path', { d: 'M8 3v17' }],
  ['path', { d: 'M16 6v16' }]
])

export const IconCalendar = makeIcon('IconCalendar', [
  ['rect', { x: '3', y: '4', width: '18', height: '18', rx: '2' }],
  ['path', { d: 'M16 2v4' }],
  ['path', { d: 'M8 2v4' }],
  ['path', { d: 'M3 10h18' }],
  ['path', { d: 'm9 16 2 2 4-4' }]
])

export const IconSettings = makeIcon('IconSettings', [
  ['circle', { cx: '12', cy: '12', r: '3' }],
  ['path', { d: 'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z' }]
])

export const IconActivity = makeIcon('IconActivity', [
  ['path', { d: 'M22 12h-4l-3 9L9 3l-3 9H2' }]
])

export const IconBell = makeIcon('IconBell', [
  ['path', { d: 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9' }],
  ['path', { d: 'M10.3 21a1.94 1.94 0 0 0 3.4 0' }]
])

export const IconBellOff = makeIcon('IconBellOff', [
  ['path', { d: 'M13.73 21a2 2 0 0 1-3.46 0' }],
  ['path', { d: 'M18.63 13A17.89 17.89 0 0 1 18 8' }],
  ['path', { d: 'M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14' }],
  ['path', { d: 'M18 8a6 6 0 0 0-9.33-5' }],
  ['line', { x1: '2', y1: '2', x2: '22', y2: '22' }]
])

export const IconSearch = makeIcon('IconSearch', [
  ['circle', { cx: '11', cy: '11', r: '8' }],
  ['path', { d: 'm21 21-4.3-4.3' }]
])

export const IconLogOut = makeIcon('IconLogOut', [
  ['path', { d: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' }],
  ['polyline', { points: '16 17 21 12 16 7' }],
  ['line', { x1: '21', y1: '12', x2: '9', y2: '12' }]
])

export const IconDownload = makeIcon('IconDownload', [
  ['path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' }],
  ['polyline', { points: '7 10 12 15 17 10' }],
  ['line', { x1: '12', y1: '15', x2: '12', y2: '3' }]
])

export const IconEdit = makeIcon('IconEdit', [
  ['path', { d: 'M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z' }]
])

export const IconTrash = makeIcon('IconTrash', [
  ['polyline', { points: '3 6 5 6 21 6' }],
  ['path', { d: 'M19 6l-2 14H7L5 6' }],
  ['path', { d: 'M10 11v6' }],
  ['path', { d: 'M14 11v6' }]
])

export const IconCheck = makeIcon('IconCheck', [
  ['polyline', { points: '20 6 9 17 4 12' }]
])

export const IconX = makeIcon('IconX', [
  ['line', { x1: '18', y1: '6', x2: '6', y2: '18' }],
  ['line', { x1: '6', y1: '6', x2: '18', y2: '18' }]
])

export const IconEye = makeIcon('IconEye', [
  ['path', { d: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' }],
  ['circle', { cx: '12', cy: '12', r: '3' }]
])

export const IconFile = makeIcon('IconFile', [
  ['path', { d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' }],
  ['polyline', { points: '14 2 14 8 20 8' }],
  ['line', { x1: '16', y1: '13', x2: '8', y2: '13' }],
  ['line', { x1: '16', y1: '17', x2: '8', y2: '17' }]
])

export const IconImage = makeIcon('IconImage', [
  ['rect', { x: '3', y: '3', width: '18', height: '18', rx: '2' }],
  ['circle', { cx: '8.5', cy: '8.5', r: '1.5' }],
  ['polyline', { points: '21 15 16 10 5 21' }]
])

export const IconPlus = makeIcon('IconPlus', [
  ['line', { x1: '12', y1: '5', x2: '12', y2: '19' }],
  ['line', { x1: '5', y1: '12', x2: '19', y2: '12' }]
])

export const IconFilter = makeIcon('IconFilter', [
  ['polygon', { points: '22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3' }]
])

export const IconChevron = makeIcon('IconChevron', [
  ['polyline', { points: '6 9 12 15 18 9' }]
])

export const IconArrowLeft = makeIcon('IconArrowLeft', [
  ['line', { x1: '19', y1: '12', x2: '5', y2: '12' }],
  ['polyline', { points: '12 19 5 12 12 5' }]
])

export const IconFileText = makeIcon('IconFileText', [
  ['path', { d: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' }],
  ['polyline', { points: '14 2 14 8 20 8' }],
  ['line', { x1: '16', y1: '13', x2: '8', y2: '13' }],
  ['line', { x1: '16', y1: '17', x2: '8', y2: '17' }],
  ['polyline', { points: '10 9 9 9 8 9' }]
])

export const IconReceipt = makeIcon('IconReceipt', [
  ['path', { d: 'M4 2v20l2-1.5L8 22l2-1.5L12 22l2-1.5L16 22l2-1.5L20 22V2l-2 1.5L16 2l-2 1.5L12 2l-2 1.5L8 2 6 3.5 4 2z' }],
  ['line', { x1: '8', y1: '8', x2: '16', y2: '8' }],
  ['line', { x1: '8', y1: '12', x2: '16', y2: '12' }],
  ['line', { x1: '8', y1: '16', x2: '13', y2: '16' }]
])

export const IconMore = makeIcon('IconMore', [
  ['circle', { cx: '12', cy: '12', r: '1' }],
  ['circle', { cx: '19', cy: '12', r: '1' }],
  ['circle', { cx: '5', cy: '12', r: '1' }]
])

export const IconAlert = makeIcon('IconAlert', [
  ['path', { d: 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z' }],
  ['line', { x1: '12', y1: '9', x2: '12', y2: '13' }],
  ['line', { x1: '12', y1: '17', x2: '12.01', y2: '17' }]
])

export const IconPhone = makeIcon('IconPhone', [
  ['path', { d: 'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z' }]
])

export const IconMail = makeIcon('IconMail', [
  ['path', { d: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z' }],
  ['polyline', { points: '22,6 12,13 2,6' }]
])

export const IconMailOpen = makeIcon('IconMailOpen', [
  ['path', { d: 'M21.2 8.4c.5.38.8.97.8 1.6v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V10a2 2 0 0 1 .8-1.6l8-6a2 2 0 0 1 2.4 0l8 6Z' }],
  ['path', { d: 'm22 10-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 10' }]
])

export const IconMapPin = makeIcon('IconMapPin', [
  ['path', { d: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z' }],
  ['circle', { cx: '12', cy: '10', r: '3' }]
])

export const IconBriefcase = makeIcon('IconBriefcase', [
  ['rect', { x: '2', y: '7', width: '20', height: '14', rx: '2' }],
  ['path', { d: 'M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16' }]
])

export const IconLock = makeIcon('IconLock', [
  ['rect', { x: '3', y: '11', width: '18', height: '11', rx: '2' }],
  ['path', { d: 'M7 11V7a5 5 0 0 1 10 0v4' }]
])

export const IconGlobe = makeIcon('IconGlobe', [
  ['circle', { cx: '12', cy: '12', r: '10' }],
  ['line', { x1: '2', y1: '12', x2: '22', y2: '12' }],
  ['path', { d: 'M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z' }]
])

export const IconTrendUp = makeIcon('IconTrendUp', [
  ['polyline', { points: '22 7 13.5 15.5 8.5 10.5 2 17' }],
  ['polyline', { points: '16 7 22 7 22 13' }]
])

export const IconDollarSign = makeIcon('IconDollarSign', [
  ['line', { x1: '12', y1: '1', x2: '12', y2: '23' }],
  ['path', { d: 'M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' }]
])

export const IconCheckCircle = makeIcon('IconCheckCircle', [
  ['path', { d: 'M22 11.08V12a10 10 0 1 1-5.93-9.14' }],
  ['polyline', { points: '22 4 12 14.01 9 11.01' }]
])

export const IconClock = makeIcon('IconClock', [
  ['circle', { cx: '12', cy: '12', r: '10' }],
  ['polyline', { points: '12 6 12 12 16 14' }]
])

export const IconMenu = makeIcon('IconMenu', [
  ['line', { x1: '3', y1: '6', x2: '21', y2: '6' }],
  ['line', { x1: '3', y1: '12', x2: '21', y2: '12' }],
  ['line', { x1: '3', y1: '18', x2: '21', y2: '18' }]
])

export const IconKey = makeIcon('IconKey', [
  ['path', { d: 'M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4' }]
])

export const IconUpload = makeIcon('IconUpload', [
  ['path', { d: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4' }],
  ['polyline', { points: '17 8 12 3 7 8' }],
  ['line', { x1: '12', y1: '3', x2: '12', y2: '15' }]
])

export const IconRefresh = makeIcon('IconRefresh', [
  ['path', { d: 'M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8' }],
  ['path', { d: 'M21 3v5h-5' }],
  ['path', { d: 'M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16' }],
  ['path', { d: 'M3 21v-5h5' }]
])
